import { FC, useMemo } from 'react';
import type { FinancialHealthStats } from '../../types/financialStats';
import { BaseChartTemplate } from './BaseChartTemplate';

// Constants for styling
const LINE_STYLES = {
  CURVED: 0.4
};

interface MinimumDebtPaymentsChartProps {
  data: FinancialHealthStats[];
  showDebug?: boolean;
  onExpandClick?: React.MutableRefObject<any>;
  monthsToShow?: number;
}

// Helper function to calculate trend line using linear regression
const calculateTrendLine = (data: number[]): number[] => {
  if (data.length <= 2) return data;
  
  // Use linear regression to calculate trend line
  let sumX = 0;
  let sumY = 0;
  let sumXY = 0;
  let sumXX = 0;
  const n = data.length;
  
  // Calculate sums for linear regression formula
  for (let i = 0; i < n; i++) {
    sumX += i;
    sumY += data[i];
    sumXY += i * data[i];
    sumXX += i * i;
  }
  
  // Calculate slope and y-intercept
  const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
  const intercept = (sumY - slope * sumX) / n;
  
  // Generate trend line points
  return data.map((_, i) => intercept + slope * i);
};

export const MinimumDebtPaymentsChart: FC<MinimumDebtPaymentsChartProps> = ({
  data,
  showDebug,
  onExpandClick,
  monthsToShow = 24
}) => {
  // Format currency for Y-axis
  const formatCurrency = (value: number) => {
    if (value === 0) return '$0';
    if (value % 1000 === 0) return `$${(value/1000)}k`;
    return `$${value.toLocaleString()}`;
  };
  
  // Format tooltip values
  const formatTooltip = (value: number) => [`$${value.toLocaleString()}`, 'Minimum Debt Payments'];
  
  // Calculate maximum for Y-axis (20% above the max value, rounded to nearest $1000)
  const calculateYMax = useMemo(() => {
    if (data.length === 0) return 5000;
    
    const maxValue = Math.max(...data.map(item => item.minimum_debt_payments || 0));
    if (maxValue === 0) return 5000;
    
    // Add 20% buffer and round up to nearest $1000
    const withBuffer = maxValue * 1.2;
    return Math.ceil(withBuffer / 1000) * 1000;
  }, [data]);
  
  // Helper to determine debt payments trend
  const getDebtTrend = (current: number, previous: number) => {
    const change = current - previous;
    const percentChange = previous > 0 ? (change / previous) * 100 : 0;
    
    if (percentChange > 10) return { direction: 'negative', label: 'Increasing Fast', color: '#EF4444' };
    if (percentChange > 3) return { direction: 'caution', label: 'Increasing', color: '#F59E0B' };
    if (percentChange > 0) return { direction: 'neutral', label: 'Slight Increase', color: '#64748B' };
    if (percentChange < -10) return { direction: 'positive', label: 'Decreasing Fast', color: '#10B981' };
    if (percentChange < -3) return { direction: 'positive', label: 'Decreasing', color: '#10B981' };
    if (percentChange < 0) return { direction: 'neutral', label: 'Slight Decrease', color: '#64748B' };
    return { direction: 'neutral', label: 'Stable', color: '#64748B' };
  };
  
  // Render the overlay with current data
  const renderOverlay = (visibleData: FinancialHealthStats[]) => {
    if (visibleData.length === 0) return null;
    
    // Calculate metrics for the overlay
    const currentPayments = visibleData[visibleData.length - 1]?.minimum_debt_payments || 0;
    const previousPayments = visibleData[visibleData.length - 2]?.minimum_debt_payments || 0;
    const monthlyChange = currentPayments - previousPayments;
    const momentum = getDebtTrend(currentPayments, previousPayments);
    
    return (
      <div className="absolute left-16 top-2 bg-white bg-opacity-95 rounded-lg shadow-sm text-left w-56">
        <div className="grid gap-2.5 p-3">
          <div>
            <div className="text-xs text-slate-500 mb-1">Current Debt Payments</div>
            <div className="text-xl font-bold text-slate-800">${currentPayments.toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</div>
          </div>
          
          <div className="flex items-center gap-4 pt-2 border-t border-slate-100">
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Monthly Change</div>
              {monthlyChange === 0 ? (
                <div className="text-sm font-medium">$0.00</div>
              ) : (
                <div className="flex items-center gap-1.5">
                  <span 
                    className="text-lg" 
                    style={{ 
                      color: momentum.direction === 'positive' ? '#10B981' : 
                             momentum.direction === 'negative' ? '#EF4444' : '#64748B'
                    }}
                  >
                    {monthlyChange > 0 ? '↑' : '↓'}
                  </span>
                  <span className="text-sm font-medium">${Math.abs(monthlyChange).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}</span>
                </div>
              )}
            </div>
            
            <div>
              <div className="text-xs text-slate-500 mb-0.5">Trend</div>
              <div className="text-sm font-medium">{momentum.label}</div>
            </div>
          </div>
        </div>
      </div>
    );
  };
  
  // Prepare chart datasets including the main line and trend line
  const getChartDatasets = useMemo(() => {
    if (!data || data.length === 0) return [];
    
    // Sort data chronologically
    const sortedData = [...data].sort((a, b) => 
      new Date(a.month).getTime() - new Date(b.month).getTime()
    );
    
    // Get the most recent months up to monthsToShow
    const visibleData = sortedData.slice(-monthsToShow);
    
    // Extract minimum debt payments values
    const values = visibleData.map(d => d.minimum_debt_payments || 0);
    
    // Calculate trend line
    const trendLine = calculateTrendLine(values);
    
    return [
      {
        // Main data line
        label: 'Minimum Debt Payments',
        data: values,
        fill: true,
        tension: LINE_STYLES.CURVED,
        borderWidth: 2.5,
        pointRadius: 3,
        pointBackgroundColor: 'rgba(255, 255, 255, 0.9)',
        pointBorderColor: 'rgba(245, 158, 11, 1)',
        pointBorderWidth: 2,
        borderColor: '#F59E0B',
        backgroundColor: 'rgba(245, 158, 11, 0.1)',
      },
      {
        // Trend line
        label: 'Trend',
        data: trendLine,
        fill: false,
        tension: 0.2,
        borderWidth: 2,
        borderDash: [6, 4],
        pointRadius: 0,
        borderColor: 'rgba(107, 114, 128, 0.8)',
        backgroundColor: 'transparent',
      }
    ];
  }, [data, monthsToShow]);
  
  return (
    <div className="h-full">
      <BaseChartTemplate
        data={data}
        showDebug={showDebug}
        monthsToShow={monthsToShow}
        onExpandClick={onExpandClick}
        title="Minimum Debt Payments"
        dataKey="minimum_debt_payments"
        color="#F59E0B"
        yAxisFormatter={formatCurrency}
        tooltipFormatter={formatTooltip}
        renderOverlay={renderOverlay}
        isCurrentFinances={true}
        min={0}
        max={calculateYMax}
        pointRadius={3}
        pointBackgroundColor="rgba(255, 255, 255, 0.9)"
        pointBorderColor="rgba(245, 158, 11, 1)"
        pointBorderWidth={2}
        borderWidth={2.5}
        tension={LINE_STYLES.CURVED}
        customDatasets={getChartDatasets}
      />
    </div>
  );
};
